/** @jsx jsx */
import React, {
	type RefObject,
	type ReactNode,
	Component,
	type ReactElement,
	type KeyboardEvent,
	type MouseEvent,
	type SyntheticEvent,
	type PropsWithChildren,
	type KeyboardEventHandler,
} from 'react';
import { styled, css, jsx } from '@compiled/react';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import keycode from 'keycode';
import { cssMap } from '@atlaskit/css';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Avatar from '@atlaskit/avatar';
import FlagFilledIcon from '@atlaskit/icon/core/flag-filled';
import ChevronDownIconOld from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIconOld from '@atlaskit/icon/glyph/chevron-right';
import FlagFilledIconOld from '@atlaskit/icon/glyph/flag-filled';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import Lozenge from '@atlaskit/lozenge';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Flex, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import { UNDEFINED } from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	type Color,
	type ColorSchema,
	PURPLE,
} from '@atlassian/jira-issue-epic-color-types/src/types.tsx';
import { transformColorToTheme } from '@atlassian/jira-issue-epic-color/src/common/utils.tsx';
import { CardKey } from '@atlassian/jira-platform-card/src/common/ui/key/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { Link as LinkComponent } from '@atlassian/react-resource-router';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import {
	getIsSoftwareThemingEnabled,
	getIsSoftwareThemingM2Enabled,
} from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import {
	zIndex,
	swimlaneHeaderHeight,
	unscheduledColumnHeaderHeight,
} from '../../common/constants/styles/index.tsx';
import type { IssueStatus } from '../../common/types.tsx';
import { getLozengeAppearanceByStatus } from '../../common/utils/lozenge-appearance/index.tsx';
import CardEpic from './epic/index.tsx';
import messages from './messages.tsx';
import { TruncatedTooltip } from './truncated-tooltip/index.tsx';
import type { IssueParent, Assignee, SwimlaneModeId } from './types.tsx';

type Props = {
	isSwimlaneHeaderClickable: boolean;
	issueKey: string | null;
	issueStatus: IssueStatus | null;
	issueParent: IssueParent | null;
	name: string;
	spotlightTargetId: string;
	showCardAmount?: boolean;
	cardAmount: number;
	icon: ReactElement | null;
	children: ReactElement | ReactElement[];
	collapsed: boolean;
	assignee: Assignee | null;
	customSwimlaneContent: ReactNode;
	customRightContent: ReactNode;
	summaryTooltip: string | null;
	// TODO Update class to functional component and use the analytics hooks
	onToggle: (analyticsEvent?: UIAnalyticsEvent) => void;
	onClick: (analyticsEvent?: UIAnalyticsEvent) => void;
	intl: IntlShape;
	isSubtask: boolean;
	link?: string;
	projectKey: string | null | undefined;
	isFlagged: boolean;
	isDone: boolean;
	wrapperStyle?: React.CSSProperties;
	innerRef?: RefObject<HTMLDivElement>;
	lozenge?: ReactElement | null;
	customLeftContent?: ReactElement | ReactNode | null;
	swimlaneMode?: SwimlaneModeId | null;
	isUnscheduledWorkColumnPanel: boolean;
	isMatrixLayout?: boolean;
	isThemed?: boolean;
};

const AVATAR_BORDER_COLOR = 'transparent';

const LOZENGE_THEMES = transformColorToTheme(
	(colorSchema: ColorSchema) => colorSchema.textBackground,
);

const Wrapper = ({
	children,
	isMatrixLayout,
}: PropsWithChildren & {
	isMatrixLayout?: boolean;
}) => {
	if (isMatrixLayout) {
		return (
			<Box
				xcss={
					// eslint-disable-next-line no-nested-ternary
					getIsSoftwareThemingM2Enabled()
						? innerWrapperStylesM2Themed.root
						: getIsSoftwareThemingEnabled()
							? innerWrapperStyles.root
							: innerWrapperStylesOld.root
				}
			>
				{children}
			</Box>
		);
	}
	return children;
};

const getLozengeThemeByColor = (color: Color) =>
	// This uses schema from src/packages/issue/epic-color/src/common/constants.js
	LOZENGE_THEMES[color] || LOZENGE_THEMES[PURPLE];

// eslint-disable-next-line jira/react/no-class-components
class Swimlane extends Component<Props> {
	static defaultProps = {
		issueKey: null,
		issueParent: null,
		issueStatus: null,
		isSwimlaneHeaderClickable: false,
		collapsed: false,
		spotlightTargetId: '',
		assignee: null,
		icon: null,
		customSwimlaneContent: null,
		customRightContent: null,
		summaryTooltip: null,
		onToggle: noop,
		onClick: noop,
		isSubtask: false,
		link: undefined,
		projectKey: undefined,
		isFlagged: false,
		isDone: false,
		wrapperStyles: {},
		lozenge: null,
		showCardAmount: true,
		isUnscheduledWorkColumnPanel: false,
		isMatrixLayout: false,
	};

	onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if ((event.code === 'Enter' || event.code === 'Space') && fg('tanuki_a11y_q4')) {
			event.preventDefault();
			// Just like toggleColumns, this is always undefined since the emitting element never has a UIAnalyticsEvent
			this.props.onToggle(undefined);
		} else if (event.keyCode === keycode('enter') || event.keyCode === keycode('space')) {
			event.preventDefault();
			// Just like toggleColumns, this is always undefined since the emitting element never has a UIAnalyticsEvent
			this.props.onToggle(undefined);
		}
	};

	onSwimlaneHeaderKeyDown = (
		event: KeyboardEvent<HTMLDivElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		if ((event.code === 'Enter' || event.code === 'Space') && fg('tanuki_a11y_q4')) {
			event.preventDefault();
			this.onLinkClick(event, undefined);
		} else if (event.keyCode === keycode('enter') || event.keyCode === keycode('space')) {
			event.preventDefault();
			this.onLinkClick(event, analyticsEvent);
		}
	};

	onLinkClick = (e: MouseEvent | KeyboardEvent, analyticsEvent?: UIAnalyticsEvent) => {
		e.stopPropagation();
		this.props.onClick(analyticsEvent);
	};

	getSwimlaneContent(): ReactElement {
		const {
			intl: { formatMessage },
			showCardAmount,
			cardAmount,
			icon,
			name,
			collapsed,
			isSwimlaneHeaderClickable,
			issueKey,
			issueParent,
			issueStatus,
			assignee,
			customSwimlaneContent,
			summaryTooltip,
			isSubtask,
			link,
			projectKey,
			isFlagged,
			isDone,
			lozenge,
			swimlaneMode,
			customLeftContent,
			isThemed,
		} = this.props;

		if (!isNil(customSwimlaneContent)) {
			return <>{customSwimlaneContent}</>;
		}

		const issueCountMessage = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.issueCountIssueTermRefresh
			: messages.issueCount;

		const cardsAmountLabel = showCardAmount
			? formatMessage(isSubtask ? messages.subtaskCount : issueCountMessage, {
					count: cardAmount || 0,
				})
			: null;

		const summary = projectKey ? `${name} (${projectKey})` : name;

		const swimlaneHeaderContentId = `boardkit-swimlane-header-${issueKey}`;

		const oldHeaderContent = (
			<>
				{(icon || (issueParent && swimlaneMode)) && (
					<Flex alignItems="center" gap="space.050">
						{icon}
						{issueParent && swimlaneMode === 'SWIMLANE_BY_PARENT_ISSUE' && (
							<Tooltip content={issueParent.summary}>
								<ColorBox color={getLozengeThemeByColor(issueParent.color)} />
							</Tooltip>
						)}
					</Flex>
				)}
				{issueKey && <CardKey text={issueKey} stopEventPropagation={false} isDone={isDone} />}
				{summaryTooltip ? (
					<Tooltip content={summaryTooltip} data-testid="platform-board-kit.ui.swimlane.tooltip">
						{isThemed && getIsSoftwareThemingEnabled() ? (
							<Box
								xcss={
									fg('jira_theming_milestone_1_improvements')
										? summarySectionStyles
										: summarySectionStylesOld
								}
								testId="platform-board-kit.ui.swimlane.summary-section"
							>
								{summary}
							</Box>
						) : (
							<SummarySection data-testid="platform-board-kit.ui.swimlane.summary-section">
								{summary}
							</SummarySection>
						)}
					</Tooltip>
				) : (
					<TruncatedTooltip content={summary}>
						{({ ref }) =>
							isThemed && getIsSoftwareThemingEnabled() ? (
								<Box
									xcss={
										fg('jira_theming_milestone_1_improvements')
											? summarySectionStyles
											: summarySectionStylesOld
									}
									ref={ref}
									testId="platform-board-kit.ui.swimlane.summary-section"
								>
									{summary}
								</Box>
							) : (
								<SummarySection
									ref={ref}
									data-testid="platform-board-kit.ui.swimlane.summary-section"
								>
									{summary}
								</SummarySection>
							)
						}
					</TruncatedTooltip>
				)}
				{lozenge && <LozengeWrapper>{lozenge}</LozengeWrapper>}
				{customLeftContent}
				{showCardAmount &&
					(isThemed && getIsSoftwareThemingEnabled() ? (
						<Box xcss={swimlaneIssuesAmountStyles}>{`(${cardsAmountLabel})`}</Box>
					) : (
						<SwimlaneIssuesAmount>{`(${cardsAmountLabel})`}</SwimlaneIssuesAmount>
					))}
				{swimlaneMode !== 'SWIMLANE_BY_PARENT_ISSUE' && issueParent && (
					<CardEpic color={issueParent.color}>{issueParent.summary}</CardEpic>
				)}
				{issueStatus && (issueStatus.name || '').toString().length > 0 && (
					<StatusSection>
						<ScreenReaderText>{formatMessage(messages.statusLozenge)}</ScreenReaderText>
						<Tooltip content={issueStatus.name}>
							<Lozenge
								appearance={getLozengeAppearanceByStatus(
									issueStatus && issueStatus.category ? issueStatus.category : UNDEFINED,
								)}
								testId="platform-board-kit.ui.swimlane.lozenge"
								isBold={isVisualRefreshEnabled()}
							>
								{issueStatus.name}
							</Lozenge>
						</Tooltip>
					</StatusSection>
				)}
				{isFlagged && (
					<FlagFilledIcon
						label={formatMessage(messages.flagged)}
						color={token('color.icon.danger')}
						LEGACY_fallbackIcon={FlagFilledIconOld}
						LEGACY_primaryColor={token('color.icon.danger')}
						LEGACY_size="small"
					/>
				)}
				{assignee && (
					<AvatarWrapper>
						<Tooltip content={assignee.name}>
							<Avatar
								src={assignee.avatarUrl}
								name={assignee.name}
								size="small"
								borderColor={AVATAR_BORDER_COLOR}
							/>
						</Tooltip>
					</AvatarWrapper>
				)}
			</>
		);

		const headerContent = fg('tanuki_a11y_q4') ? (
			<div id={swimlaneHeaderContentId} css={headerContentStyles.root}>
				{oldHeaderContent}
			</div>
		) : (
			<Flex gap="space.100" alignItems="center" xcss={oldheaderContentStyles}>
				{oldHeaderContent}
			</Flex>
		);

		const handleHeaderKeydownHandler: KeyboardEventHandler<HTMLDivElement> = (event) => {
			if (event.code === 'Enter' || event.code === 'Space') {
				event.preventDefault();
				this.onLinkClick(event, undefined);
			}
		};

		const renderHeader = () => {
			if (isSwimlaneHeaderClickable) {
				if (issueKey) {
					return (
						<LinkButtonWithAnalytics
							data-testid="platform-board-kit.ui.swimlane.link-button"
							onClick={this.onLinkClick}
						>
							{fg('tanuki_a11y_q4') && (
								<Box
									aria-labelledby={swimlaneHeaderContentId}
									xcss={swimlaneContentWrapper.pseudo}
									role="button"
									onClick={this.onLinkClick}
									tabIndex={0}
									onKeyDown={handleHeaderKeydownHandler}
								/>
							)}
							{headerContent}
						</LinkButtonWithAnalytics>
					);
				}

				if (link) {
					return (
						<LinkWithAnalytics
							href={link}
							onClick={this.onLinkClick}
							// @ts-expect-error - Type '(event: KeyboardEvent<HTMLDivElement>, analyticsEvent: UIAnalyticsEvent) => void' is not assignable to type 'KeyboardEventHandler<HTMLAnchorElement>'.
							onKeyDown={this.onSwimlaneHeaderKeyDown}
						>
							{headerContent}
						</LinkWithAnalytics>
					);
				}
			}

			return headerContent;
		};

		const renderChevronIcon = () => (
			<Box xcss={[isVisualRefreshEnabled() && iconSpacing]}>
				{collapsed ? (
					<ChevronRightIcon
						LEGACY_fallbackIcon={ChevronRightIconOld}
						LEGACY_primaryColor={token('color.icon')}
						color={token('color.icon')}
						label=""
					/>
				) : (
					<ChevronDownIcon
						LEGACY_fallbackIcon={ChevronDownIconOld}
						LEGACY_primaryColor={token('color.icon')}
						color={token('color.icon')}
						label=""
					/>
				)}
			</Box>
		);

		return (
			<SwimlaneContent
				css={swimlaneContentWrapper.root}
				onClick={this.toggleColumns}
				data-testid="platform-board-kit.ui.swimlane.swimlane-content"
				{...(!fg('tanuki_a11y_q4')
					? {
							role: 'button',
							tabIndex: 0,
							onKeyDown: this.onKeyDown,
							'aria-expanded': !collapsed,
							'aria-labelledby': swimlaneHeaderContentId,
						}
					: {})}
			>
				{fg('tanuki_a11y_q4') && (
					<Box
						xcss={swimlaneContentWrapper.pseudo}
						role="button"
						onClick={this.toggleColumns}
						tabIndex={0}
						onKeyDown={this.onKeyDown}
						aria-expanded={!collapsed}
						aria-labelledby={swimlaneHeaderContentId}
						testId="platform-board-kit.ui.swimlane.swimlane-content-pseudo"
					/>
				)}
				{renderChevronIcon()}

				{renderHeader()}
			</SwimlaneContent>
		);
	}

	toggleColumns = (_: SyntheticEvent<HTMLElement>) => {
		// TODO: Remove this comment post fg/tanuki_a11y_q4 clean up
		// I've confirmed that this analytic event is always undefined since emitting element never has a UIAnalyticsEvent
		this.props.onToggle(undefined);
	};

	renderSwimlaneInner = () => {
		const { isUnscheduledWorkColumnPanel, spotlightTargetId, customRightContent } = this.props;

		return (
			<Box
				testId="platform-board-kit.ui.swimlane.swimlane-inner"
				xcss={[
					SwimlaneInnerStyles,
					isUnscheduledWorkColumnPanel && SwimlaneInnerSingleColumnWidthStyles,
					!isUnscheduledWorkColumnPanel && SwimlaneInnerFullWidthStyles,
				]}
			>
				<SpotlightTarget name={spotlightTargetId}>{this.getSwimlaneContent()}</SpotlightTarget>
				<EngagementSpotlight engagementId={spotlightTargetId} />
				{Boolean(customRightContent) && (
					<SwimlaneRightContentContainer>{customRightContent}</SwimlaneRightContentContainer>
				)}
			</Box>
		);
	};

	renderSwimlaneBar = () => {
		const { isUnscheduledWorkColumnPanel, isThemed } = this.props;
		return (
			<div
				css={[
					swimlaneBarStyles,
					isUnscheduledWorkColumnPanel && swimlaneBarUnscheduledColumnStyles,
					isThemed && getIsSoftwareThemingEnabled()
						? swimlaneThemingStyles
						: swimlaneThemingStylesOld,
				]}
			>
				{this.renderSwimlaneInner()}
			</div>
		);
	};

	render() {
		const { children, collapsed, wrapperStyle, innerRef, isMatrixLayout } = this.props;

		return (
			<div
				data-testid="platform-board-kit.ui.swimlane.swimlane-wrapper"
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={wrapperStyle}
				ref={innerRef}
			>
				<Wrapper isMatrixLayout={isMatrixLayout}>
					<>
						<SwimlaneShadow isSoftwareThemingEnabled={getIsSoftwareThemingEnabled()} />
						{getIsSoftwareThemingEnabled() ? (
							<div css={[SwimlaneShadowMaskStyles]} />
						) : (
							<SwimlaneShadowMaskOld />
						)}
					</>

					{this.renderSwimlaneBar()}
					{!collapsed && (
						<Flex testId="platform-board-kit.ui.swimlane.swimlane-columns">{children}</Flex>
					)}
				</Wrapper>
			</div>
		);
	}
}

export default injectIntl(Swimlane);

const swimlaneShadowSize = 2;
const headerHeight = swimlaneHeaderHeight;

const innerWrapperStylesOld = cssMap({
	root: {
		backgroundColor: token('elevation.surface'),
		borderRadius: '6px',
		marginBottom: token('space.100'),
		paddingBottom: token('space.050'),
	},
});

const innerWrapperStyles = cssMap({
	root: {
		// @ts-expect-error Type 'string' is not assignable to type '"transparent" | "currentcolor" | "var(--ds-background-accent-lime-subtlest-hovered)" | "var(--ds-background-accent-lime-subtler-hovered)" | "var(--ds-background-accent-lime-subtle-hovered)" | ... 191 more ... | undefined'.ts(2322)
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundColor: Tokens.COLOR_ELEVATION_SURFACE,
		borderRadius: '6px',
		marginBottom: token('space.100'),
		paddingBottom: token('space.050'),
	},
});

const innerWrapperStylesM2Themed = cssMap({
	root: {
		// @ts-expect-error Type 'string' is not assignable to type '"transparent" | "currentcolor" | "var(--ds-background-accent-lime-subtlest-hovered)" | "var(--ds-background-accent-lime-subtler-hovered)" | "var(--ds-background-accent-lime-subtle-hovered)" | ... 191 more ... | undefined'.ts(2322)
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundColor: Tokens.COLOR_ELEVATION_SURFACE,
		borderRadius: '6px',
		marginBottom: token('space.100'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SwimlaneContent = styled.div({
	display: 'inline-flex',
	flex: 1,
	minWidth: 0,
	alignItems: 'center',
	marginLeft: token('space.025'),
	maxWidth: '100%',
});

const SwimlaneInnerStyles = xcss({
	display: 'flex',
	gap: 'space.100',
	alignItems: 'center',
	flex: 1,
});

const SwimlaneInnerSingleColumnWidthStyles = xcss({
	minWidth: '100%',
	maxWidth: '100%',
});

const SwimlaneInnerFullWidthStyles = xcss({
	// So things dont overlap on smaller screens
	minWidth: '700px',
	maxWidth: `min(100%, calc(var(--board-scroll-element-width, 2000) * 1px - ${token('space.500')})`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const SwimlaneShadow = styled.div<{ isSoftwareThemingEnabled: boolean }>(
	{
		display: 'block',

		backgroundColor: token('color.blanket'),
		opacity: 0.2,
		height: `${swimlaneShadowSize}px`,
		position: 'sticky',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		top: `${headerHeight * 2}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: zIndex.stickySwimlaneBar,
		pointerEvents: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ isSoftwareThemingEnabled }) =>
		isSoftwareThemingEnabled
			? {
					marginRight: token('space.075'),
				}
			: {
					margin: `0 ${token('space.075')}`,
				},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SwimlaneShadowMaskOld = styled.div({
	display: 'block',
	backgroundColor: token('elevation.surface'),
	height: `${swimlaneShadowSize}px`,
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	top: `${headerHeight}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: zIndex.stickySwimlaneBar,
	pointerEvents: 'none',
	marginTop: token('space.negative.025'),
	marginRight: 0,
	marginBottom: 0,
	marginLeft: 0,
});

const SwimlaneShadowMaskStyles = css({
	display: 'block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundColor: Tokens.COLOR_ELEVATION_SURFACE,
	height: `${swimlaneShadowSize}px`,
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	top: `${headerHeight}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: zIndex.stickySwimlaneBar,
	pointerEvents: 'none',
	marginTop: token('space.negative.025'),
	marginRight: 0,
	marginBottom: 0,
	marginLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarWrapper = styled.div({
	height: '28px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SwimlaneIssuesAmount = styled.div({
	font: token('font.body.UNSAFE_small'),
	color: token('color.text.subtle'),
});

const swimlaneIssuesAmountStyles = xcss({
	font: token('font.body.UNSAFE_small'),
	// @ts-expect-error - need to apply custom tokens for sufficient text contrast in themed projects
	color:
		Tokens.COLOR_TEXT_SUBTLE /* eslint-disable-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values */,
});

const swimlaneBarStyles = css({
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
	'&:focus': {
		boxShadow: `inset 0 0 0 2px ${token('color.border.focused')}`,
		outline: 'none',
	},
	position: 'sticky',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: zIndex.stickySwimlaneBar,
	paddingTop: token('space.025'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${headerHeight - swimlaneShadowSize}px`,
	marginTop: token('space.negative.025'),
	marginBottom: token('space.025'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	top: getIsSoftwareThemingM2Enabled() ? 0 : `${headerHeight}px`,
});

// TODO: combine this style into swimlaneBarStyles above on cleanup of getIsSoftwareThemingEnabled
const swimlaneThemingStyles = css({
	backgroundColor: Tokens.COLOR_ELEVATION_SURFACE, // eslint-disable-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	marginBottom: 'unset',
});

const swimlaneThemingStylesOld = css({
	borderRadius: '3px',
	backgroundColor: token('elevation.surface'),
});

const swimlaneBarUnscheduledColumnStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	top: `${unscheduledColumnHeaderHeight}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkButtonOld = styled.button({
	background: 'none',
	border: 'none',
	paddingTop: 0,
	paddingRight: token('space.100'),
	paddingBottom: 0,
	paddingLeft: 0,
	font: 'inherit',
	cursor: 'pointer',
	minWidth: 0,
});

const LinkButton = componentWithFG(
	'tanuki_a11y_q4',
	({
		children,
		'data-testid': testId,
		onClick,
	}: {
		children: ReactNode;
		'data-testid': string;
		onClick: (e: MouseEvent | KeyboardEvent) => void;
	}) => {
		return (
			// eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, @atlassian/a11y/interactive-element-not-keyboard-focusable
			<div css={linkButtonContainerStyles.root} data-testid={testId} onClick={onClick}>
				{children}
			</div>
		);
	},
	LinkButtonOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Link = styled(LinkComponent)({
	background: 'none',
	border: 'none',
	paddingTop: 0,
	paddingRight: token('space.100'),
	paddingBottom: 0,
	paddingLeft: 0,
	font: 'inherit',
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&, &:hover': {
		textDecoration: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SummarySection = styled.div({
	flex: 1,
	minWidth: 0,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const summarySectionStyles = xcss({
	flex: 1,
	minWidth: '0px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	// @ts-expect-error - need to apply custom tokens for sufficient text contrast in themed projects
	color:
		Tokens.COLOR_TEXT /* eslint-disable-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values */,
	':hover': {
		textDecoration: 'underline',
	},
});

const summarySectionStylesOld = xcss({
	flex: 1,
	minWidth: '0px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	// @ts-expect-error - need to apply custom tokens for sufficient text contrast in themed projects
	color:
		Tokens.COLOR_TEXT /* eslint-disable-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values */,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatusSection = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& span': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
		lineHeight: `${gridSize * 2}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div': {
		display: 'flex',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SwimlaneRightContentContainer = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	paddingTop: 0,
	paddingRight: token('space.100'),
	paddingBottom: 0,
	paddingLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeWrapper = styled.span({
	marginLeft: token('space.100'),
	display: 'inline-block',
});

const LinkButtonWithAnalytics = ComponentWithAnalytics('swimlaneHeader', {
	onClick: 'clicked',
})(LinkButton);

const LinkWithAnalytics = ComponentWithAnalytics('swimlaneHeader', {
	onClick: 'clicked',
})(Link);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColorBox = styled.div<{ color: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) => props.color,
	width: '1.25rem',
	height: '1.25rem',
	borderRadius: token('border.radius.050'),
});

const oldheaderContentStyles = xcss({ minWidth: '0' });

const headerContentStyles = cssMap({
	root: {
		display: 'flex',
		boxSizing: 'border-box',
		gap: token('space.100'),
		alignItems: 'center',
		minWidth: '0px',
	},
});

const iconSpacing = xcss({
	margin: 'space.050',
	width: '20px',
	display: 'flex',
	justifyContent: 'center',
});
const swimlaneContentWrapper = cssMap({
	root: {
		position: 'relative',
	},
	pseudo: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: -1,
	},
});

const linkButtonContainerStyles = cssMap({
	root: {
		position: 'relative',
		paddingTop: token('space.0'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		minWidth: '0px',
		color: token('color.text'),
	},
});
